import { DAY, fDT, fLong, fShort, greeting, span, toInstant, dateOf, addDays, toLocal } from '@nandanam/domain';
import { useStore } from '../app/store';
import { Icon } from '../ui/icons';
import { BAR, TINT, attention, calItems, dayText, dayStart, layoutBars, live, nextArrival, pkgLabel, weekdayShort, dayNum } from '../app/selectors';

export function Dashboard() {
  const S = useStore();
  const t0 = dayStart(S), today = dateOf(S.now);
  const att = attention(S);
  const lv = live(S), nx = nextArrival(S);
  const now = lv
    ? { title: `${S.cust(lv.cust).name.split(' ')[0]} family is ${lv.kind === 'stay' ? 'staying' : 'here'}`, sub: `${pkgLabel(lv)} · ${lv.guests} guests · until ${fDT(span(lv).end)}`, open: () => S.nav('booking', { id: lv.id }) }
    : { title: 'The property is free', sub: 'No guests on site', open: () => S.nav('calendar') };
  const recs = S.state.records;
  const cnt = (f: (p: string, r: (typeof recs)[number]) => boolean) => recs.filter(r => f(S.phase(r), r)).length;
  const pipeline = [
    { n: cnt(p => p === 'enquiry'), short: 'enquiries', label: 'Open enquiries', go: () => S.nav('enquiries') },
    { n: cnt(p => p === 'hold'), short: 'holds', label: 'Active holds', go: () => S.nav('enquiries', { filter: 'hold' }) },
    { n: cnt(p => p === 'upcoming'), short: 'upcoming', label: 'Confirmed upcoming', go: () => S.nav('bookings') },
    { n: cnt((p, r) => p === 'completed' && toInstant(span(r).end) > S.now - 30 * DAY), short: 'done', label: 'Completed in 30 days', go: () => S.nav('bookings', { filter: 'completed' }) },
  ];
  const upcoming = calItems(S, S.now, t0 + 40 * DAY, false).filter(x => x.ph !== 'inprogress' && x.start >= S.now).slice(0, 7);
  const N = 14;
  const bars = layoutBars(calItems(S, t0, t0 + N * DAY, false), t0, N);
  const lanes = bars.reduce((m, b) => Math.max(m, b.lane), 0) + 1;
  const days = Array.from({ length: N }, (_, i) => addDays(today, i));

  return (
    <div className="page" style={{ paddingTop: 0 }}>
      <section className="hero" style={{ background: `linear-gradient(90deg, rgba(47,79,79,0.94) 0%, rgba(47,79,79,0.82) 38%, rgba(47,79,79,0.25) 70%, rgba(47,79,79,0) 100%), url('assets/pool.jpg') center 60% / cover` }}>
        <div className="hero-text">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span className="hero-date num">{fLong(today)}</span>
            <h1 className="hero-greet">{greeting(toLocal(S.now))}</h1>
          </div>
          <button className="hero-now" onClick={now.open}>
            <div className="t">{now.title}</div>
            <div className="s">{now.sub}</div>
          </button>
          <div className="hero-minis">
            <button type="button" className="mini" onClick={() => S.nav('calendar')}><div className="k">Today</div><div className="v">{dayText(S, today).text}</div></button>
            <button type="button" className="mini" style={{ minWidth: 0 }} disabled={!nx} onClick={() => nx && S.nav('booking', { id: nx.id })}><div className="k">Next arrival</div><div className="v ellipsis">{nx ? `${S.cust(nx.cust).name} · ${fDT(span(nx).start)}` : '—'}</div></button>
            <button type="button" className="mini" onClick={() => document.getElementById('needs-you')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}><div className="k">Needs you</div><div className="v">{att.length ? `${att.length} to act on` : 'All clear'}</div></button>
          </div>
        </div>
      </section>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-start' }}>
        <section id="needs-you" className="card" style={{ flex: '1.8 1 520px', scrollMarginTop: 80 }}>
          <div className="section-head">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><h3 className="card-title">Needs your attention</h3><span className="count-badge num">{att.length}</span></div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {pipeline.map(p => <button key={p.short} className="chip-btn" title={p.label} onClick={p.go}><b className="num">{p.n}</b>{p.short}</button>)}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {att.length === 0 && <div className="empty"><b>All clear</b>Nothing needs you right now.</div>}
            {att.map((a, i) => {
              const [bg, fg, ic] = TINT[a.tint];
              return (
                <div key={a.id + i} className="att-row">
                  <span className="tile lg" style={{ background: bg, color: fg }}><Icon name={ic} size={18} /></span>
                  <div style={{ flex: '1 1 220px', minWidth: 0, cursor: 'pointer' }} onClick={() => S.nav('booking', { id: a.id })}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 14.5, fontWeight: 600, letterSpacing: '-0.01em' }}>{a.title}</span>
                      <span className="pill num" style={{ background: bg, color: fg, fontWeight: 500, fontSize: 11 }}>{a.kicker}</span>
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3 }}>{a.detail}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', flexWrap: 'wrap' }}>
                    <button className="btn" onClick={a.a2.run}>{a.a2.label}</button>
                    <button className="btn primary" disabled={!S.canEdit} onClick={a.a1.run}>{a.a1.label}</button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <aside style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: 22, minWidth: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 10 }}>
            {[['Record payment', 'rupee', '#e1ebe9', '#2F4F4F', 'payment'], ['Block dates', 'lock', '#dff1f9', '#26708f', 'block'], ['Add expense', 'receipt', '#f5f5dc', '#5c5630', 'expense']].map(([l, ic, bg, fg, o]) => (
              <button key={l} className="quick" onClick={() => S.open(o!, {})}><span className="tile" style={{ background: bg, color: fg }}><Icon name={ic!} size={18} /></span>{l}</button>
            ))}
          </div>
          <div className="card" style={{ padding: '20px 20px 12px' }}>
            <div className="section-head" style={{ marginBottom: 8 }}>
              <h4 className="card-sub">Coming up</h4>
              <button className="chip-btn" onClick={() => S.nav('calendar')}>Calendar</button>
            </div>
            {upcoming.length === 0 && <div className="muted" style={{ fontSize: 13, padding: '8px 0 12px' }}>Nothing booked in the next few weeks.</div>}
            {upcoming.map(u => {
              const key = u.ph === 'hold' ? 'hold' : u.k === 'stay' ? 'stay' : u.k === 'event' ? 'event' : 'block';
              const [bg, fg] = ({ hold: ['#f5f5dc', '#5c5630'], stay: ['#e1ebe9', '#2F4F4F'], event: ['#dff1f9', '#26708f'], block: ['#eef1f4', '#4e5d6b'] } as const)[key];
              const d = dateOf(u.start), r = u.rec ? S.rec(u.id) : null;
              return (
                <button key={u.id} className="upc" onClick={() => (u.rec ? S.nav('booking', { id: u.id }) : S.nav('calendar', { focus: d }))}>
                  <div className="date-chip" style={{ background: bg, color: fg }}><span>{weekdayShort(d)}</span><b className="num">{dayNum(d)}</b></div>
                  <div style={{ minWidth: 0, textAlign: 'left' }}>
                    <div className="ellipsis" style={{ fontSize: 14, fontWeight: 600 }}>{u.title}</div>
                    <div className="ellipsis" style={{ fontSize: 12, color: 'var(--muted-2)' }}>{u.ph === 'hold' ? 'Hold' : u.word} · {fShort(toLocal(u.start), toLocal(u.end))} · {r ? pkgLabel(r) : u.sub}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>
      </div>

      <section className="card">
        <div className="section-head">
          <h3 className="card-title">Next 14 days</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {(['event', 'stay', 'hold', 'block', 'maint'] as const).map(k => (
              <span key={k} className="legend"><span style={{ background: BAR[k].bg, border: `1px ${BAR[k].bs} ${BAR[k].bc}` }} />{BAR[k].word}</span>
            ))}
          </div>
        </div>
        <div className="strip">
          {days.map((d, i) => {
            const info = dayText(S, d), st = info.info.state;
            const label = st === 'free' ? 'Free' : st === 'full' ? 'Booked' : info.text.replace('Free ', '');
            const bg = i === 0 ? '#2F4F4F' : st === 'free' ? '#f1f7f6' : st === 'full' ? '#f2f4f6' : '#f7f7ee';
            const fg = i === 0 ? '#F5F5DC' : st === 'free' ? '#2f6a5f' : '#4e5d6b';
            return (
              <button key={d} className="strip-day" style={{ background: bg, color: fg }} onClick={() => S.nav('calendar', { focus: d })}>
                <div className="dw">{weekdayShort(d)}</div><div className="dn num">{dayNum(d)}</div><div className="ds ellipsis">{label}</div>
              </button>
            );
          })}
        </div>
        <div className="strip-bars" style={{ height: Math.max(1, lanes) * 32 }}>
          {bars.map(b => {
            const st = BAR[b.k];
            return (
              <button key={b.id} className="bar" title={`${b.title} — ${b.sub}`} onClick={() => (b.rec ? S.nav('booking', { id: b.id }) : S.nav('calendar', { focus: dateOf(b.start) }))}
                style={{ left: `calc(${b.left}% + 3px)`, width: `calc(${b.width}% - 6px)`, top: b.lane * 32, height: 28, background: st.bg, border: `1px ${st.bs} ${st.bc}`, color: st.fg }}>
                <Icon name={st.icon} size={13} /><span className="ellipsis" style={{ textDecoration: b.k === 'expired' || b.k === 'cancelled' ? 'line-through' : 'none' }}>{b.title}</span>
                {b.ph === 'hold' && <span style={{ opacity: 0.7 }}>· hold</span>}
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
